import React from 'react';
import { Link } from 'react-router-dom';
import { 
  PenTool, 
  Code, 
  Repeat, 
  Share2, 
  Layers, 
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Upload,
  FileImage
} from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import WireframeExample from '../assets/wireframe_example.jpg';

const FeatureCard = ({ icon: Icon, title, description, color = "blue" }) => {
  const bgColorClass = `bg-${color}-50`;
  const textColorClass = `text-${color}-500`;

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-6">
      <div className={`mb-4 rounded-lg p-3 inline-block ${bgColorClass}`}>
        <Icon className={`h-6 w-6 ${textColorClass}`} />
      </div>
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const Features = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar with features as the active page */}
      <MainNavbar activePage="features" />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold mb-6">AI-Powered Prototyping</h1>
            <p className="text-xl mb-6 opacity-90">
              Transform text descriptions into interactive UI prototypes in minutes
            </p>
            <Link to="/signup" className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
              Try For Free
            </Link>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Key Features</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform helps you create interactive prototypes faster and easier than ever before
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={PenTool} 
              title="Describe Your Vision" 
              description="Simply describe your UI in plain language or use our structured editor to specify exactly what you need."
              color="yellow"
            />
            
            <FeatureCard 
              icon={FileImage} 
              title="Upload Wireframes" 
              description="Transform hand-drawn sketches, wireframes, or UI mockups into fully functional prototype designs instantly."
              color="purple"
            />
            
            <FeatureCard 
              icon={Sparkles} 
              title="AI-Generated Designs" 
              description="Our AI transforms your descriptions into fully-functional UI designs in seconds, no design skills required."
              color="orange"
            />
            
            <FeatureCard 
              icon={Repeat} 
              title="Iterative Refinement" 
              description="Refine designs by telling the AI what to change, with immediate updates based on your feedback."
              color="red"
            />
            
            <FeatureCard 
              icon={Layers} 
              title="Flow Creation" 
              description="Connect multiple views into interactive flows to demonstrate user journeys and navigation paths."
              color="blue"
            />
            
            <FeatureCard 
              icon={Code} 
              title="HTML Export" 
              description="Download clean, responsive HTML/CSS code that's ready for implementation in your projects."
              color="green"
            />
            
            <FeatureCard 
              icon={Share2} 
              title="Shareable Links" 
              description="Create protected links to share your prototypes with team members and stakeholders."
              color="purple"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From idea to interactive prototype in just three steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-orange-500">1</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Describe</h3>
              <p className="text-gray-600">
                Use plain language to describe what you want, choose our structured editor, or upload wireframe images.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-orange-500">2</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Generate & Refine</h3>
              <p className="text-gray-600">
                Our AI creates your design instantly. Give feedback to refine it until it perfectly matches your vision.
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-orange-500">3</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Share & Export</h3>
              <p className="text-gray-600">
                Create interactive flows, share with stakeholders via secure links, or export HTML code for development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Details */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold mb-6">AI-Powered Design Generation</h2>
              <ul className="space-y-4">
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Natural Language Input</span>
                    <p className="text-gray-600 mt-1">Describe your UI in everyday language without technical jargon</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Structured Editor Option</span>
                    <p className="text-gray-600 mt-1">Use guided form input for more precise control when needed</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">AI Clarification Questions</span>
                    <p className="text-gray-600 mt-1">The AI asks targeted questions to better understand your needs</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Multi-Device Designs</span>
                    <p className="text-gray-600 mt-1">Create designs optimized for desktop or mobile</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <div className="bg-white rounded shadow p-4 mb-3">
                <p className="text-sm text-gray-500 mb-2">User Description:</p>
                <p className="text-gray-800 bg-gray-50 p-3 rounded text-sm">
                  "Create a dashboard for a fitness app with a profile section at the top, workout statistics in the middle, and a weekly chart at the bottom."
                </p>
              </div>
              <div className="flex justify-center my-4">
                <Sparkles className="text-orange-500 w-8 h-8" />
              </div>
              <div className="bg-black text-white p-4 rounded shadow">
  <p className="text-sm text-gray-400 mb-2">Generated Design:</p>
  {/* Replace the placeholder <div> with this <img> element */}
  <img
    src={WireframeExample}
    alt="Generated Design Preview"
    className="w-full h-auto object-cover border border-gray-700 rounded"
  />
</div>
            </div>
          </div>

          {/* New Wireframe Upload Feature Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <div className="bg-white rounded shadow-sm overflow-hidden">
                <div className="bg-gray-800 text-white p-3 text-sm">Wireframe to Code</div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-full h-32 bg-gray-100 rounded border border-gray-300 flex items-center justify-center mb-2">
                        <Upload className="h-8 w-8 text-gray-400" />
                      </div>
                      <span className="text-xs text-gray-500">Wireframe Image</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="relative w-full h-32 bg-gray-100 rounded border border-gray-300 flex items-center justify-center mb-2">
                        <div className="absolute inset-0 m-2 bg-white p-1 rounded shadow-sm">
                          <div className="w-full h-full bg-gray-50 rounded-sm flex items-center justify-center">
                            <Code className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">Generated HTML</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-700">
                    <p className="font-medium mb-1">AI Analysis:</p>
                    <p className="text-xs">Navigation bar detected at top. 3 card components in middle section. Footer with contact details at bottom...</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Wireframe to Interactive UI</h2>
              <ul className="space-y-4">
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Photo Upload</span>
                    <p className="text-gray-600 mt-1">Upload photos of hand-drawn sketches, whiteboard designs, or digital mockups</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Smart Component Detection</span>
                    <p className="text-gray-600 mt-1">AI automatically identifies UI elements, layouts, and component hierarchies</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Detailed Analysis</span>
                    <p className="text-gray-600 mt-1">Get an explanation of how your wireframe was interpreted for better understanding</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Editable Results</span>
                    <p className="text-gray-600 mt-1">Revise and refine the generated code with the same tools you'd use for text descriptions</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl font-bold mb-6">Powerful Revision Tools</h2>
              <ul className="space-y-4">
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Simple Instructions</span>
                    <p className="text-gray-600 mt-1">Revise designs with plain language like "make the header larger" or "change the button color"</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Structured Revisions</span>
                    <p className="text-gray-600 mt-1">Organize changes by type: replace, add, remove, adjust, or refine</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Version History</span>
                    <p className="text-gray-600 mt-1">Compare different revisions to see what's changed and revert if needed</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Instant Updates</span>
                    <p className="text-gray-600 mt-1">See your changes reflected immediately in the updated design</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm order-1 lg:order-2">
              <div className="bg-white rounded shadow-sm overflow-hidden">
                <div className="bg-gray-800 text-white p-3 text-sm">Revision Request</div>
                <div className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-red-100 flex-shrink-0 mr-2">
                        <div className="w-4 h-4 rounded-full bg-red-500 opacity-50"></div>
                      </div>
                      <p className="text-sm">Replace the profile picture with an avatar</p>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-orange-100 flex-shrink-0 mr-2">
                        <div className="w-4 h-4 rounded-full bg-orange-500 opacity-50"></div>
                      </div>
                      <p className="text-sm">Add a goals section beneath the statistics</p>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex-shrink-0 mr-2">
                        <div className="w-4 h-4 rounded-full bg-green-500 opacity-50"></div>
                      </div>
                      <p className="text-sm">Adjust the chart to show more data points</p>
                    </div>
                  </div>
                  <button className="mt-4 bg-black text-white px-3 py-1 rounded text-sm">Submit Revisions</button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Interactive Flows</h2>
              <ul className="space-y-4">
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Multi-View Sequences</span>
                    <p className="text-gray-600 mt-1">Connect multiple screens to demonstrate user journeys and workflows</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Drag & Drop Organization</span>
                    <p className="text-gray-600 mt-1">Easily arrange and reorder screens in your user flow</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Version Selection</span>
                    <p className="text-gray-600 mt-1">Choose specific versions of each view to include in your flow</p>
                  </div>
                </li>
                <li className="flex">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Shareable Presentations</span>
                    <p className="text-gray-600 mt-1">Present your flow as an interactive slideshow for stakeholders</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
              <div className="bg-white p-4 rounded shadow-sm">
                <div className="flex justify-between mb-4">
                  <div className="text-lg font-medium">User Registration Flow</div>
                  <div className="text-sm text-gray-500">3 Views</div>
                </div>
                <div className="relative flex items-center justify-between mb-2">
                  <div className="w-24 h-16 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-500">Welcome</div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <div className="w-24 h-16 bg-orange-100 rounded border flex items-center justify-center text-xs text-gray-500">Sign Up</div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <div className="w-24 h-16 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-500">Dashboard</div>
                </div>
                <div className="flex justify-between mt-4">
                  <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm">Prev</button>
                  <div className="text-sm text-gray-500">1 of 3</div>
                  <button className="px-3 py-1 bg-black text-white rounded text-sm">Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharing and Export */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Share & Export</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Multiple options to share your work with stakeholders and developers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="rounded-lg p-3 inline-block bg-blue-50 mb-4">
                <Share2 className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Secure Sharing</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Create shareable links for team members and clients</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Password protection for sensitive prototypes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Set expiration dates for temporary access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Track who viewed your shared prototypes</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="rounded-lg p-3 inline-block bg-green-50 mb-4">
                <Code className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Developer Handoff</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Export clean, semantic HTML and CSS code</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Tailwind CSS integration for easy styling</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Responsive layouts that work across devices</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Well-structured code for easy implementation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Do I need design experience to use dessino.ai?</h3>
              <p className="text-gray-600">
                No design experience needed. Simply describe what you want, upload a wireframe, or use our structured editor. Our AI will generate professional designs based on your input.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">What types of wireframes can I upload?</h3>
              <p className="text-gray-600">
                You can upload photos of hand-drawn sketches, digital wireframes, screenshots from design tools, whiteboard designs, or any image that visually represents your UI layout.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">How do I revise my designs?</h3>
              <p className="text-gray-600">
                After generating your initial design, use our revision tools to make changes. You can replace elements, add new ones, remove unwanted items, or adjust existing components with simple instructions.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Can I create mobile-responsive designs?</h3>
              <p className="text-gray-600">
                Yes, you can specify your target device (Desktop or Mobile) and all designs will be optimized accordingly with responsive layouts.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">How do flows work?</h3>
              <p className="text-gray-600">
                Flows let you connect multiple views to create interactive user journeys. You can drag and drop views into a sequence, select specific versions, and share the entire flow as a cohesive prototype.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Design Process?</h2>
          <p className="text-xl mb-8">
            Get started for free today and see how dessino.ai can help you create better prototypes faster.
          </p>
          <Link to="/signup" className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Start Free Trial
          </Link>
          <p className="mt-4 text-sm text-white/80">No credit card required. 10 free generations to get you started.</p>
        </div>
      </section>

      {/* Footer with the reusable component */}
      <MainFooter />
    </div>
  );
};

export default Features;